/**
 * Compact accordion in the Panier summary (same pattern as Réduction).
 *
 * Theme-owned slot: #fixatech-gift-inline. Do not use BOGOS's
 * #freegifts-main-page-container — that ID switches the app embed
 * from popup to page mode and intercepts add-to-cart storewide.
 *
 * SHOW iff: cartOpen && offerToPick && !giftQuotaFilled && !dismissedThisOpen
 *
 * giftQuotaFilled is true only when every qualifying slider offer has
 * nothing left. Remaining per offer_root, in order: (a) BOGOS globals
 * bogos_total_gift_<root> minus bogos_added_gift_<root>, (b) cached
 * fg-gifts:show-slider detail.giftsOffer[].giftsAvailable, (c)
 * number_of_gift_can_receive × (is_multiply ? floor(units/min_amount)
 * : units>=min ? 1 : 0), capped by max_amount, minus gifts for that
 * offer. A failed read fails open (do not hide if BOGOS rendered cards).
 *
 * Gift ATC: Glider refresh (peek sizing) rebuilds slides and drops BOGOS's
 * per-button click listeners. Hosted Ajouter is handled here like collection
 * card ATC — call BOGOS addGiftToCart (needs ?sca_add_gift + properties),
 * then dispatch theme cart:update so Panier morphs.
 *
 * The BOGOS popup must stay on document.body whenever it is not actively
 * hosted in the fold — cart section morphs destroy nodes left inside Panier.
 */
(() => {
  const POPUP_ID = 'freegifts-main-popup-container';
  const PAGE_ID = 'fixatech-gift-inline';
  const FOLD_ID = 'fixatech-gift-fold';
  const CLOSE_SEL = '.sca-close-modal-fg, .bogos-slider-close-btn';
  const ADD_BTN_SEL = [
    '.bogos-gift-item-add-btn',
    '#freegifts-main-popup-container .btn-add-to-cart',
    '#fixatech-gift-inline .btn-add-to-cart',
  ].join(', ');
  const GIFT_CARD_SEL = [
    '.bogos-offer-gift-item',
    '.bogos-gift-item-add-btn',
    '.bogos-gift-item-image',
    '#fg-gift-items .btn-add-to-cart',
    '.freegifts-main-container .product-item',
    '.sca-modal-dialog-fg .product-item',
    '.bogos-gifts-slider-container .glider-slide',
  ].join(', ');
  const themeGlobal = () => (typeof Theme !== 'undefined' ? Theme : window.Theme);
  const CART_URL = themeGlobal()?.routes?.cart_url
    ? `${themeGlobal().routes.cart_url.replace(/\/$/, '')}.js`
    : '/cart.js';
  const SLIDES_TO_SHOW = 2.15;

  const watchedNodes = new WeakSet();
  let offerAvailable = null;
  let giftsInCart = 0;
  let giftQuotaFilled = false;
  let dismissedThisOpen = false;
  let cartOpenKnown = false;
  let retryTimers = [];
  let evalGen = 0;
  let fetchGen = 0;
  let cartBound = null;
  let cartAttrObserver = null;
  let needsRestack = false;
  let addGiftToCartFn = null;
  let giftAddInFlight = false;
  let giftsOfferCache = null;
  let lastCartItems = [];
  let foldDetailsOpen = true;
  let foldToggleBound = null;
  let bogosEvalTimer = null;
  let bogosEvalDebounce = null;

  /* Re-entrancy guard.

     The popup observer in watch() listens for class/style/hidden with
     subtree: true, and its handler calls scheduleEvaluate(). Every branch
     downstream of evaluate() writes exactly those attributes on the observed
     node or its descendants: sizeInlineSlider and hideInlineChrome set inline
     styles on .glider / .glider-slide / .bogos-offer-gift-item, promote()
     flips fold.hidden and adds sca-is-visible-fg, and neutralizeParkedPopup
     sets pointer-events / visibility / opacity / z-index on the popup itself.

     So the handler mutates the attributes it is watching, which re-queues the
     handler, which mutates again. Adding a gift is what makes it unbounded:
     giftQuotaFilled flips true, hidePicker() runs, pickerIsShown() goes false, and
     the handler's suppression guard stops firing. That is the page freeze.

     Fix: count our own mutation passes and have the observer ignore anything
     delivered while a pass is in flight. The decrement is deferred to a macro
     task because a pass spans requestAnimationFrame and setTimeout hops, so a
     synchronous reset would clear the flag before the records are delivered. */
  let selfMutating = 0;
  const withSelfMutation = (fn) => {
    selfMutating += 1;
    try {
      return fn();
    } finally {
      setTimeout(() => {
        selfMutating = Math.max(0, selfMutating - 1);
      }, 0);
    }
  };

  const popup = () => document.getElementById(POPUP_ID);
  const cartDialog = () =>
    document.querySelector('cart-drawer-component dialog, dialog.cart-drawer__dialog');

  /* On /cart there is no dialog to open, so the cart is inherently open.
     cartIsOpen gates 13 call sites, which is why widening this ONE predicate
     is the whole cart-page fix rather than a scatter of selector changes.
     Template name can carry a suffix (cart.alternate), so compare the stem.
     Same idiom the theme uses itself in header-actions.liquid. */
  const templateName = () =>
    String(
      themeGlobal()?.template?.name ||
        document.getElementById('MainContent')?.dataset.template ||
        document.querySelector('main[data-template]')?.dataset.template ||
        ''
    );
  const onCartPage = () => templateName().split('.')[0] === 'cart';
  const cartIsOpen = () => onCartPage() || Boolean(cartDialog()?.open);
  const foldEl = () => document.getElementById(FOLD_ID);

  const ensureFold = () => {
    let fold = foldEl();
    if (fold?.isConnected) return fold;
    const actions = document.querySelector(
      'cart-drawer-component .cart-actions, dialog.cart-drawer__dialog .cart-actions'
    );
    if (!actions) return null;

    fold = document.createElement('accordion-custom');
    fold.id = FOLD_ID;
    fold.className = 'fixatech-gift-fold';
    fold.hidden = true;
    fold.innerHTML = `
      <details class="details">
        <summary class="fixatech-gift-fold__summary cart-discount__summary">
          <span class="cart-discount__label cart-primary-typography">Cadeau</span>
          <span class="cart-totals__icon" aria-hidden="true">+</span>
        </summary>
        <div class="details-content fixatech-gift-fold__content">
          <div id="${PAGE_ID}" class="fixatech-gift-inline"></div>
        </div>
      </details>
    `;
    actions.insertBefore(fold, actions.firstChild);
    const divider = document.createElement('div');
    divider.className = 'cart-actions__divider fixatech-gift-fold__divider';
    fold.after(divider);
    return fold;
  };

  const inlineSlot = () => {
    ensureFold();
    let slot = document.getElementById(PAGE_ID);
    if (slot?.isConnected) return slot;
    const fold = foldEl();
    const mount = fold?.querySelector('.details-content') || fold;
    if (!mount) return null;
    slot = document.createElement('div');
    slot.id = PAGE_ID;
    slot.className = 'fixatech-gift-inline';
    mount.appendChild(slot);
    return slot;
  };

  const popupHasGiftCards = (el) => Boolean(el?.querySelector(GIFT_CARD_SEL));

  const giftSurface = () => {
    const popupEl = popup();
    const pageEl = document.getElementById(PAGE_ID);
    if (popupHasGiftCards(popupEl)) return popupEl;
    if (popupHasGiftCards(pageEl)) return pageEl;
    return popupEl || pageEl;
  };

  const pickerIsShown = () => {
    const fold = foldEl();
    const slot = document.getElementById(PAGE_ID);
    if (!fold || fold.hidden) return false;
    const el = popup();
    return Boolean((el && slot?.contains(el)) || popupHasGiftCards(slot) || popupHasGiftCards(fold));
  };

  const markInlineOpen = (open) => {
    withSelfMutation(() => {
      document.documentElement.classList.toggle('fixatech-gift-inline-open', open);
    });
  };

  const resetPopupPresentation = (el) => {
    if (!el) return;
    el.classList.remove('sca-is-visible-fg');
    ['display', 'visibility', 'opacity', 'pointer-events', 'z-index'].forEach((prop) => {
      el.style.removeProperty(prop);
    });
    el.querySelectorAll(
      '.glider, .glider-track, .bogos-offer-gift-items-container, .bogos-slider-offers-container, .glider-slide, .bogos-offer-gift-item, .product-item'
    ).forEach((node) => {
      ['width', 'min-width', 'max-width', 'flex-basis', 'height', 'min-height', 'max-height'].forEach(
        (prop) => {
          node.style.removeProperty(prop);
        }
      );
    });
  };

  /* BOGOS modal often locks body; after park/hide clear leftovers so Panier
     clicks work. Leave dialog[open] alone — native modal handles that. */
  const unlockHostScroll = () => {
    [document.documentElement, document.body].forEach((node) => {
      if (!node) return;
      ['overflow', 'overflow-x', 'overflow-y', 'padding-right', 'touch-action'].forEach((prop) => {
        if (node.style.getPropertyValue(prop)) node.style.removeProperty(prop);
      });
    });
    document.documentElement.classList.remove('bogos-modal-open', 'sca-modal-open-fg');
    document.body.classList.remove('bogos-modal-open', 'sca-modal-open-fg');
  };

  /* Unhosted popup must never intercept clicks — even if BOGOS re-applies
     sca-is-visible-fg / inline visibility after gift ATC + cart morph. */
  const neutralizeParkedPopup = (el) => {
    if (!el) return;
    resetPopupPresentation(el);
    const hosted = Boolean(document.getElementById(PAGE_ID)?.contains(el));
    if (hosted) return;
    el.style.setProperty('pointer-events', 'none', 'important');
    el.style.setProperty('visibility', 'hidden', 'important');
    el.style.setProperty('opacity', '0', 'important');
    el.style.setProperty('z-index', '-1', 'important');
    unlockHostScroll();
  };

  const parkPopup = () =>
    withSelfMutation(() => {
      const el = popup();
      if (!el) return;
      if (el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
      neutralizeParkedPopup(el);
    });

  const hidePicker = () =>
    withSelfMutation(() => {
      markInlineOpen(false);
      parkPopup();
      const fold = foldEl();
      if (fold) fold.hidden = true;
      unlockHostScroll();
    });

  const bindFoldToggle = (fold) => {
    if (!fold || fold === foldToggleBound) return;
    foldToggleBound = fold;
    fold.addEventListener('toggle', (event) => {
      if (event.target instanceof HTMLDetailsElement) foldDetailsOpen = event.target.open;
    });
  };

  const restoreFoldOpen = () => {
    const details = foldEl()?.querySelector('details');
    if (details) details.open = foldDetailsOpen;
  };

  const isCartTarget = (target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.matches('cart-drawer-component') ||
        target.classList.contains('cart-drawer__dialog') ||
        target.closest('cart-drawer-component')
    );
  };

  const listOffers = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === 'object') return Object.values(raw);
    return [];
  };

  const giftsFromDetail = (detail) => {
    if (!detail) return null;
    if (Array.isArray(detail.gifts)) return detail.gifts;
    if (Array.isArray(detail.arrGiftShow)) return detail.arrGiftShow;
    if (Array.isArray(detail.giftsOffer)) {
      return detail.giftsOffer.flatMap((offer) =>
        Array.isArray(offer?.arrGiftShow) ? offer.arrGiftShow : []
      );
    }
    if (Array.isArray(detail.giftsOffer?.arrGiftShow)) return detail.giftsOffer.arrGiftShow;
    if (Array.isArray(detail.popup)) return detail.popup;
    return null;
  };

  const giftsOfferEntries = (detail) => {
    const raw = detail?.giftsOffer;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === 'object') return [raw];
    return null;
  };

  const offerToPick = (el) => {
    /* Observed cards outrank the cached flag, in both directions.

       onOfferAdded sets offerAvailable = false. Nothing guarantees BOGOS fires
       show-slider or updated again afterwards, so a bare `false` short-circuit
       went sticky: add a gift, empty the cart, re-add the product, and the
       offer was refused even though BOGOS had rendered its cards. Reading the
       DOM first makes the flag a hint rather than an authority, so the layer
       recovers on its own instead of waiting for an event that may not come. */
    if (popupHasGiftCards(el)) return true;
    if (offerAvailable === true) return true;
    if (offerAvailable === false) return false;
    return false;
  };

  const itemIsBogosGift = (item) => {
    const properties = item?.properties;
    if (properties && typeof properties === 'object') {
      const marked = Object.keys(properties).some((key) => {
        const lower = key.toLowerCase();
        return lower.startsWith('_bogos') || lower === '_free_gift' || lower === '_freegift';
      });
      if (marked) return true;
    }
    return String(item?.handle || '').includes('sca_clone_freegift');
  };

  const cartItemsFromEvent = (event) => {
    const resource = event?.detail?.resource;
    if (resource && Array.isArray(resource.items)) return resource.items;
    if (Array.isArray(event?.detail?.items)) return event.detail.items;
    return null;
  };

  /* Remaining gifts per offer_root, not a single cart-wide cap.

     cebf8fe read the largest gift_settings.number_of_gift_can_receive (1
     on Makita Y-00359) and ignored offer_conditions[].is_multiply, so the
     picker hid after the first gift even when 2–3 kits had earned 2–3.
     Flipping the BOGOS admin setting is not enough for multiply mode.

     Prefer BOGOS's own numbers (globals, then cached giftsAvailable). Fall
     back to number_of_gift_can_receive × multiply, then subtract gifts.
     Hide only when every qualifying picker offer is spent. A failed read
     leaves giftQuotaFilled false so cards on the page still open Cadeau. */
  const remainingFromGlobals = (variables, offerRoot) => {
    if (!variables || typeof variables !== 'object' || offerRoot == null) return null;
    const total = Number(variables[`bogos_total_gift_${offerRoot}`]);
    if (!Number.isFinite(total)) return null;
    const addedRaw = variables[`bogos_added_gift_${offerRoot}`];
    const added = Number.isFinite(Number(addedRaw)) ? Number(addedRaw) : 0;
    return Math.max(0, total - added);
  };

  const remainingFromCache = (cache, offerRoot) => {
    if (!Array.isArray(cache) || offerRoot == null) return null;
    const root = String(offerRoot);
    const rows = cache.filter((row) => String(row?.offer_root) === root);
    if (!rows.length) return null;
    const values = rows.map((row) => Number(row.giftsAvailable)).filter((n) => Number.isFinite(n));
    if (!values.length) return null;
    return Math.max(0, values.reduce((sum, n) => sum + n, 0));
  };

  /* Count units, not line items: BOGOS can put quantity 2 on one gift line. */
  const countGiftsInCart = (items) =>
    (items || []).reduce(
      (total, item) => (itemIsBogosGift(item) ? total + (Number(item?.quantity) || 1) : total),
      0
    );

  const variantIdsFromNarrow = (narrow) => {
    const ids = new Set();
    if (!narrow || typeof narrow !== 'object') return ids;
    const value = narrow.value;
    if (!value || typeof value !== 'object') return ids;
    const entries = Array.isArray(value) ? value : Object.values(value);
    for (const entry of entries) {
      if (entry == null) continue;
      if (typeof entry === 'number' || typeof entry === 'string') {
        ids.add(String(entry));
        continue;
      }
      if (entry.id != null) ids.add(String(entry.id));
      for (const variant of entry.variants || []) {
        if (variant?.id != null) ids.add(String(variant.id));
      }
    }
    return ids;
  };

  const qualifyingUnits = (items, offer) => {
    const conditions = Array.isArray(offer?.offer_conditions) ? offer.offer_conditions : [];
    const ids = new Set();
    for (const condition of conditions) {
      for (const narrow of condition?.product_narrow || []) {
        variantIdsFromNarrow(narrow).forEach((id) => ids.add(id));
      }
    }
    if (!ids.size) return 0;
    return (items || []).reduce((sum, item) => {
      if (itemIsBogosGift(item)) return sum;
      const variantId = item?.variant_id ?? item?.id;
      const productId = item?.product_id;
      if (ids.has(String(variantId)) || (productId != null && ids.has(String(productId)))) {
        return sum + (Number(item?.quantity) || 0);
      }
      return sum;
    }, 0);
  };

  const computedAllowance = (offer, units) => {
    const per = Number(offer?.gift_settings?.number_of_gift_can_receive);
    const giftsPer = Number.isFinite(per) && per > 0 ? per : 1;
    const conditions = Array.isArray(offer?.offer_conditions) ? offer.offer_conditions : [];
    if (!conditions.length) return null;
    let best = 0;
    let saw = false;
    for (const condition of conditions) {
      const unit = condition?.unit;
      if (unit && unit !== 'number_of_products') continue;
      const min = Number(condition?.min_amount);
      const minAmt = Number.isFinite(min) && min > 0 ? min : 1;
      const maxRaw = condition?.max_amount;
      const max = maxRaw == null || maxRaw === '' ? null : Number(maxRaw);
      const multiply = Boolean(condition?.is_multiply);
      let multiplier = 0;
      if (units >= minAmt) {
        multiplier = multiply ? Math.floor(units / minAmt) : 1;
        if (Number.isFinite(max) && max > 0) {
          const maxMult = multiply ? Math.floor(max / minAmt) : 1;
          multiplier = Math.min(multiplier, maxMult);
        }
      }
      saw = true;
      best = Math.max(best, giftsPer * multiplier);
    }
    return saw ? best : null;
  };

  const offerRootOf = (offer) => {
    if (!offer) return null;
    if (offer.offer_root != null) return String(offer.offer_root);
    if (offer.id != null) return String(offer.id);
    return null;
  };

  const giftOfferIdFromItem = (item) => {
    const props = item?.properties;
    if (!props || typeof props !== 'object') return null;
    const raw = props._bogos_trigger_id || props._bogos_offer_id || null;
    return raw == null || raw === '' ? null : String(raw);
  };

  const giftVariantId = (item) => item?.variant_id ?? item?.id;

  const offerOwnsGiftVariant = (offer, variantId) => {
    if (variantId == null) return false;
    const id = String(variantId);
    const collect = (node) =>
      Boolean(
        node &&
          (String(node.original_variant_shopify_id) === id ||
            String(node.variant_shopify_id) === id ||
            String(node.id) === id)
      );
    for (const gift of offer?.gift_items || []) {
      if (collect(gift)) return true;
    }
    for (const product of offer?.gift_products || []) {
      for (const variant of product?.variants || []) {
        if (collect(variant)) return true;
      }
    }
    return false;
  };

  const countGiftsForOffer = (items, offer, arrOfferAdded) => {
    const root = offerRootOf(offer);
    if (Array.isArray(arrOfferAdded) && arrOfferAdded.length) {
      const matched = arrOfferAdded.filter(
        (row) => row?.belongs_to_offer != null && String(row.belongs_to_offer) === root
      );
      if (matched.length) {
        return matched.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
      }
    }
    const gifts = (items || []).filter(itemIsBogosGift);
    const keyed = gifts.filter((item) => giftOfferIdFromItem(item) === root);
    if (keyed.length) {
      return keyed.reduce((sum, item) => sum + (Number(item?.quantity) || 1), 0);
    }
    const owned = gifts.filter((item) => offerOwnsGiftVariant(offer, giftVariantId(item)));
    if (owned.length) {
      return owned.reduce((sum, item) => sum + (Number(item?.quantity) || 1), 0);
    }
    return null;
  };

  const isPickerOffer = (offer) => {
    if (!offer || offer.is_offer_shipping) return false;
    return offer?.gift_settings?.customer_will_receive !== 'auto_add_all_gifts';
  };

  const pickerOffers = (offers, cache) => {
    const all = listOffers(offers);
    if (Array.isArray(cache) && cache.length) {
      const roots = new Set(cache.map((row) => String(row?.offer_root)).filter(Boolean));
      const matched = all.filter((offer) => roots.has(offerRootOf(offer)));
      if (matched.length) return matched;
    }
    return all.filter(isPickerOffer);
  };

  const remainingForOffer = ({ offer, items, variables, cache, arrOfferAdded }) => {
    const root = offerRootOf(offer);
    const fromGlobals = remainingFromGlobals(variables, root);
    if (fromGlobals != null) return fromGlobals;
    const fromCache = remainingFromCache(cache, root);
    if (fromCache != null) return fromCache;
    const units = qualifyingUnits(items, offer);
    const allowance = computedAllowance(offer, units);
    if (allowance == null) return null;
    const perOffer = countGiftsForOffer(items, offer, arrOfferAdded);
    const gifts = perOffer == null ? countGiftsInCart(items) : perOffer;
    return Math.max(0, allowance - gifts);
  };

  const quotaFilled = ({ offers, items, variables, cache, arrOfferAdded }) => {
    try {
      const list = pickerOffers(offers, cache);
      if (!list.length) return false;
      const itemList = items || [];
      const cartKnown = itemList.length > 0;
      let known = 0;
      for (const offer of list) {
        const remaining = remainingForOffer({
          offer,
          items: itemList,
          variables,
          cache,
          arrOfferAdded,
        });
        if (remaining == null) return false;
        if (remaining > 0) return false;
        const fromBogos =
          remainingFromGlobals(variables, offerRootOf(offer)) != null ||
          remainingFromCache(cache, offerRootOf(offer)) != null;
        if (!cartKnown && !fromBogos) return false;
        known += 1;
      }
      return known > 0;
    } catch (_) {
      return false;
    }
  };

  const bogosApp = () => (typeof window !== 'undefined' ? window.FGSECOMAPP : undefined) || {};

  const computeQuotaFilled = (items) =>
    quotaFilled({
      offers: bogosApp().offers,
      items: items || lastCartItems,
      variables: bogosApp().variables,
      cache: giftsOfferCache,
      arrOfferAdded: bogosApp().arrOfferAdded,
    });

  const applyGiftFromItems = (items) => {
    lastCartItems = Array.isArray(items) ? items : [];
    giftsInCart = countGiftsInCart(lastCartItems);
    giftQuotaFilled = computeQuotaFilled(lastCartItems);
  };

  const clearGiftsAvailableCache = () => {
    giftsOfferCache = null;
  };

  const cacheGiftsOfferFromDetail = (detail) => {
    const entries = giftsOfferEntries(detail);
    if (entries) giftsOfferCache = entries;
  };

  const refreshGiftInCart = () => {
    const id = ++fetchGen;
    return fetch(CART_URL, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (id !== fetchGen) return;
        if (cart && Array.isArray(cart.items)) applyGiftFromItems(cart.items);
      })
      .catch(() => {
        /* keep last known giftQuotaFilled */
      });
  };

  const captureAddGiftFn = (detail) => {
    if (typeof detail?.addGiftToCartFunc === 'function') {
      addGiftToCartFn = detail.addGiftToCartFunc;
    }
  };

  const resolveAddGiftFn = () => {
    if (typeof addGiftToCartFn === 'function') return addGiftToCartFn;
    const helper = window.FGSECOMAPP?.helper;
    if (typeof helper?.addGiftToCart === 'function') {
      return helper.addGiftToCart.bind(helper);
    }
    return null;
  };

  /* Match Horizon product-form / cart-items: cart:update without sections
     triggers sectionRenderer.renderSection. Also fire cart:updated like
     fixatech-card ATC for anything listening to that name. */
  const notifyThemeCartUpdate = () => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: 'fixatech-gift',
          data: { source: 'fixatech-gift' },
        },
      })
    );
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  };

  const hideInlineChrome = (el) => {
    if (!el) return;
    el.querySelectorAll(
      [
        '.fg-section-title',
        '.bogos-slider-info',
        '.bogos-slider-info-title',
        '.bogos-slider-info-subtitle',
        '.bogos-slider-offer-title',
        '.bogos-slider-header-info',
        /* The wrappers too: their padding survives the title being hidden.
           Both are needed and the names are easy to confuse.
           -slider-offer-header wraps one offer's title; -gifts-slider-header is
           the outer wrapper around -header-info plus the close button. */
        '.bogos-slider-offer-header',
        '.bogos-gifts-slider-header',
        '.bogos-badge-title',
        '[class*="slider-info"]',
        '[class*="section-title"]',
        '[class*="slider-offer-title"]',
        '.sca-close-modal-fg',
        '.bogos-slider-close-btn',
        '.bogos-brand-mark-gift-slider-v2',
        '.brand-mark',
        '.glider-dots',
        '.dots',
      ].join(', ')
    ).forEach((node) => {
      node.style.setProperty('display', 'none', 'important');
      node.setAttribute('aria-hidden', 'true');
    });
  };

  const getGliderInstance = (node) =>
    node?._glider || node?.glider || node?.__glider || null;

  const forcePeekGlider = (node, slideWidth) => {
    const instance = getGliderInstance(node);
    if (!instance) return;
    const opt = instance.opt || instance.options;
    if (opt) {
      opt.slidesToShow = SLIDES_TO_SHOW;
      opt.slidesToScroll = 1;
      if (slideWidth) opt.itemWidth = slideWidth;
    }
    if (typeof instance.refresh === 'function') {
      try {
        instance.refresh(true);
      } catch (_) {
        /* ignore */
      }
    }
    if (slideWidth) {
      instance.slideWidth = slideWidth;
      if (instance.opt) instance.opt.itemWidth = slideWidth;
    }
  };

  const sizeInlineSlider = (el) => {
    const slot = document.getElementById(PAGE_ID);
    const measured = slot?.clientWidth || el.clientWidth || 0;
    if (measured < 80) return null;
    const trackWidth = measured;
    const slideWidth = Math.floor(trackWidth / SLIDES_TO_SHOW);
    el.querySelectorAll(
      '.glider, .bogos-offer-gift-items-container, .bogos-slider-offers-container'
    ).forEach((node) => {
      node.style.setProperty('width', `${trackWidth}px`, 'important');
      node.style.setProperty('min-height', '0', 'important');
      node.style.setProperty('height', 'auto', 'important');
    });
    const slides = el.querySelectorAll('.glider-slide');
    if (slides.length) {
      slides.forEach((node) => {
        node.style.setProperty('width', `${slideWidth}px`, 'important');
        node.style.setProperty('min-width', `${slideWidth}px`, 'important');
        node.style.setProperty('max-width', `${slideWidth}px`, 'important');
        node.style.setProperty('flex', `0 0 ${slideWidth}px`, 'important');
        node.style.setProperty('flex-basis', `${slideWidth}px`, 'important');
        node.style.setProperty('min-height', '0', 'important');
        node.style.setProperty('height', 'auto', 'important');
      });
      el.querySelectorAll('.bogos-offer-gift-item, .product-item').forEach((node) => {
        if (node.classList.contains('glider-slide')) return;
        node.style.setProperty('width', '100%', 'important');
        node.style.setProperty('min-width', '0', 'important');
        node.style.setProperty('max-width', '100%', 'important');
        node.style.removeProperty('flex-basis');
        node.style.setProperty('height', 'auto', 'important');
      });
    } else {
      el.querySelectorAll('.bogos-offer-gift-item, .product-item').forEach((node) => {
        node.style.setProperty('width', `${slideWidth}px`, 'important');
        node.style.setProperty('min-width', '0', 'important');
        node.style.setProperty('max-width', `${slideWidth}px`, 'important');
        node.style.setProperty('height', 'auto', 'important');
      });
    }
    return slideWidth;
  };

  const refreshGiftSlider = (el) => {
    const run = () =>
      withSelfMutation(() => {
      hideInlineChrome(el);
      const slideWidth = sizeInlineSlider(el);
      el.querySelectorAll('.glider').forEach((node) => {
        forcePeekGlider(node, slideWidth);
      });
      sizeInlineSlider(el);
      el.querySelectorAll('.glider').forEach((node) => {
        const instance = getGliderInstance(node);
        if (instance?.opt && instance.opt.slidesToShow !== SLIDES_TO_SHOW) {
          instance.opt.slidesToShow = SLIDES_TO_SHOW;
          instance.opt.slidesToScroll = 1;
          if (slideWidth) instance.opt.itemWidth = slideWidth;
          if (typeof instance.refresh === 'function') instance.refresh(true);
          sizeInlineSlider(el);
        }
      });
      });
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
      setTimeout(run, 80);
      setTimeout(run, 250);
      setTimeout(run, 600);
    });
  };

  const promote = (el) =>
    withSelfMutation(() => {
    const fold = ensureFold();
    const slot = el?.id === PAGE_ID ? el : inlineSlot();
    if (!slot || !fold) return false;
    const alreadyInline = (el === slot || el.parentElement === slot) && !fold.hidden;
    if (el && el !== slot && el.parentElement !== slot) slot.appendChild(el);
    if (el && slot.contains(el)) resetPopupPresentation(el);
    fold.hidden = false;
    bindFoldToggle(fold);
    restoreFoldOpen();
    requestAnimationFrame(restoreFoldOpen);
    markInlineOpen(true);
    if (el && el !== slot) el.classList.add('sca-is-visible-fg');
    if (alreadyInline && !needsRestack) {
      if (el) refreshGiftSlider(el);
      return true;
    }
    needsRestack = false;
    if (el) refreshGiftSlider(el);
    return true;
    });

  const shouldShow = (el) =>
    cartIsOpen() && Boolean(el) && offerToPick(el) && !giftQuotaFilled && !dismissedThisOpen;

  const evaluate = async () => {
    const gen = ++evalGen;
    if (!cartIsOpen() || dismissedThisOpen || giftQuotaFilled) {
      if (giftQuotaFilled || dismissedThisOpen) foldDetailsOpen = true;
      hidePicker();
      return;
    }
    await refreshGiftInCart();
    if (gen !== evalGen) return;
    if (!cartIsOpen() || dismissedThisOpen || giftQuotaFilled) {
      if (giftQuotaFilled || dismissedThisOpen) foldDetailsOpen = true;
      hidePicker();
      return;
    }
    const node = giftSurface();
    /* Never open the fold without cards to put in it.

       This branch used to accept `offerAvailable === true` on its own, so when
       an offer event landed before BOGOS rendered its slider we promoted the
       empty inline slot: fold.hidden = false, details open, nothing inside.
       That is the blank Cadeau block, and it was self-sustaining, because the
       next evaluate took the same branch and promoted empty again while the
       early return skipped the hidePicker below.

       Cards present is now the only thing that opens it. When an offer is
       expected but not yet rendered the fold stays shut and the retry timers
       in scheduleEvaluate re-check, which is the correct degraded state. */
    if (shouldShow(node) && popupHasGiftCards(node)) {
      promote(node);
      return;
    }
    if (!pickerIsShown()) hidePicker();
  };

  const cancelRetries = () => {
    retryTimers.forEach(clearTimeout);
    retryTimers = [];
  };

  const scheduleEvaluate = () => {
    cancelRetries();
    evaluate();
    [80, 250, 600, 1200].forEach((ms) => {
      retryTimers.push(setTimeout(evaluate, ms));
    });
  };

  /* Kit qty changes fire Horizon cart:update and BOGOS stop_offer trim.
     Evaluate after BOGOS finishes (end-process / bogos:gifts-end), not on
     the theme morph alone. Fall back if those events never arrive. */
  const scheduleEvaluateAfterBogos = () => {
    cancelRetries();
    clearTimeout(bogosEvalTimer);
    bogosEvalTimer = setTimeout(scheduleEvaluate, 500);
  };

  const onBogosFinished = () => {
    clearTimeout(bogosEvalTimer);
    if (!cartIsOpen()) return;
    clearTimeout(bogosEvalDebounce);
    bogosEvalDebounce = setTimeout(scheduleEvaluate, 50);
  };

  const onCartOpened = () => {
    const firstOpen = !cartOpenKnown;
    cartOpenKnown = true;
    if (firstOpen) dismissedThisOpen = false;
    needsRestack = true;
    attach();
    scheduleEvaluate();
  };

  const onCartClosed = () => {
    cartOpenKnown = false;
    dismissedThisOpen = false;
    foldDetailsOpen = true;
    cancelRetries();
    hidePicker();
  };

  const syncCartOpenFromDom = () => {
    if (cartIsOpen()) onCartOpened();
    else if (cartOpenKnown) onCartClosed();
  };

  const watch = (el) => {
    if (!el || watchedNodes.has(el)) return;
    watchedNodes.add(el);
    new MutationObserver((records) => {
      /* Suppress only our OWN writes, which are attribute writes.

         Everything this layer changes on an observed node is an attribute:
         style from sizeInlineSlider and neutralizeParkedPopup, class from
         resetPopupPresentation, hidden on the fold, aria-hidden from
         hideInlineChrome. BOGOS rendering its gift cards is a childList
         mutation. So dropping every record during our pass would also blind
         us to the render we are waiting for, which is how an empty fold
         could stay empty. Ignore attribute records while a pass is in
         flight; always honour childList. */
      const ours = selfMutating > 0 && records.every((r) => r.type === 'attributes');
      if (ours) return;
      if (!cartIsOpen()) return;
      if (pickerIsShown() && popupHasGiftCards(el)) return;
      if (popupHasGiftCards(el)) offerAvailable = true;
      scheduleEvaluate();
    }).observe(el, {
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
      childList: true,
      subtree: true,
    });
  };

  const bindCartDialog = (cart) => {
    if (!cart || cart === cartBound) return;
    cartBound = cart;
    cart.addEventListener('close', onCartClosed);
    cart.addEventListener('toggle', (event) => {
      if (event.newState === 'open') onCartOpened();
      else if (event.newState === 'closed') onCartClosed();
    });
    cartAttrObserver?.disconnect();
    cartAttrObserver = new MutationObserver(syncCartOpenFromDom);
    cartAttrObserver.observe(cart, { attributes: true, attributeFilter: ['open'] });
    if (cart.open) onCartOpened();
  };

  const attach = () => {
    document.getElementById('fixatech-gift-host')?.remove();
    ensureFold();
    const el = popup();
    if (el) {
      if (giftQuotaFilled || dismissedThisOpen || !pickerIsShown()) parkPopup();
      watch(el);
    }
    const slot = document.getElementById(PAGE_ID);
    if (slot && slot !== el) watch(slot);
    const fold = foldEl();
    if (fold && fold !== el && fold !== slot) watch(fold);
    bindCartDialog(cartDialog());
  };

  const onOfferShow = (event) => {
    captureAddGiftFn(event.detail);
    cacheGiftsOfferFromDetail(event.detail);
    const gifts = giftsFromDetail(event.detail);
    offerAvailable = gifts ? gifts.length > 0 : popupHasGiftCards(popup()) || true;
    if (gifts && gifts.length === 0) offerAvailable = false;
    if (cartIsOpen()) scheduleEvaluate();
  };

  const onOfferUpdated = (event) => {
    captureAddGiftFn(event.detail);
    cacheGiftsOfferFromDetail(event.detail);
    const gifts = giftsFromDetail(event.detail);
    if (gifts) offerAvailable = gifts.length > 0;
    else if (popupHasGiftCards(popup())) offerAvailable = true;
    if (cartIsOpen()) scheduleEvaluate();
  };

  const onOfferAdded = (event) => {
    /* BOGOS fires fg-gifts:added even when handleAddGift returns false. */
    if (event?.detail && 'gift' in event.detail && !event.detail.gift) return;
    /* Optimistic bump so the picker reacts before /cart.js confirms; the next
       refreshGiftInCart replaces it with the real count. Only hide once every
       qualifying offer is spent, otherwise the buyer keeps picking. */
    giftsInCart += 1;
    clearGiftsAvailableCache();
    giftQuotaFilled = computeQuotaFilled(lastCartItems);
    if (giftQuotaFilled) {
      offerAvailable = false;
      foldDetailsOpen = true;
      cancelRetries();
      hidePicker();
    } else {
      scheduleEvaluate();
    }
  };

  const root = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : null;
  if (root) {
    root.FixatechBogosGiftLogic = {
      listOffers,
      giftsFromDetail,
      giftsOfferEntries,
      remainingFromGlobals,
      remainingFromCache,
      itemIsBogosGift,
      countGiftsInCart,
      qualifyingUnits,
      computedAllowance,
      offerRootOf,
      countGiftsForOffer,
      isPickerOffer,
      pickerOffers,
      remainingForOffer,
      quotaFilled,
      themeGlobal,
      templateName,
      onCartPage,
    };
  }

  if (typeof document === 'undefined') return;

  ['fg-gifts:show-slider', 'fg-gifts:updated', 'fg-gifts:added'].forEach((name) => {
    const handler =
      name === 'fg-gifts:added' ? onOfferAdded : name === 'fg-gifts:updated' ? onOfferUpdated : onOfferShow;
    document.addEventListener(name, handler);
    window.addEventListener(name, handler);
  });

  ['fg-cart:end-process', 'bogos:gifts-end'].forEach((name) => {
    document.addEventListener(name, onBogosFinished);
    window.addEventListener(name, onBogosFinished);
  });

  /* Hosted Ajouter: document delegation survives Glider rebuilds. Call
     BOGOS addGiftToCart (gift properties), then theme cart:update. */
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const addBtn = event.target.closest(ADD_BTN_SEL);
      if (!addBtn) return;
      const el = popup();
      const slot = document.getElementById(PAGE_ID);
      if (!(el?.contains(addBtn) || slot?.contains(addBtn))) return;
      if (!pickerIsShown() && !cartIsOpen()) return;

      const addFn = resolveAddGiftFn();
      const variantId = Number(addBtn.getAttribute('data-variant'));
      const offerId = addBtn.getAttribute('data-offer');
      if (!addFn || !variantId || !offerId) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (giftAddInFlight || addBtn.disabled) return;

      const card =
        addBtn.closest('.bogos-offer-gift-item, .product-item, .glider-slide') || addBtn.parentElement;
      const qtyInput = card?.querySelector(
        '.bogos-gift-item-quantity-input, .bogos-gift-quantity-input'
      );
      let qty = 1;
      if (qtyInput) {
        const parsed = parseInt(qtyInput.value, 10);
        if (!Number.isNaN(parsed) && parsed > 0) qty = parsed;
      }

      const label = addBtn.innerHTML;
      giftAddInFlight = true;
      addBtn.disabled = true;
      addBtn.classList.add('bogos-gift-adding');

      Promise.resolve(addFn(variantId, qty, offerId))
        .then(() => refreshGiftInCart())
        .then(() => {
          notifyThemeCartUpdate();
          if (giftQuotaFilled) {
            offerAvailable = false;
            foldDetailsOpen = true;
            cancelRetries();
            hidePicker();
            return;
          }
          addBtn.disabled = false;
          addBtn.classList.remove('bogos-gift-adding');
          addBtn.innerHTML = label;
        })
        .catch((err) => {
          console.error('fixatech gift ATC', err);
          addBtn.disabled = false;
          addBtn.classList.remove('bogos-gift-adding');
          addBtn.innerHTML = label;
        })
        .finally(() => {
          giftAddInFlight = false;
        });
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest(CLOSE_SEL)) return;
      if (!popup()?.contains(event.target)) return;
      dismissedThisOpen = true;
      foldDetailsOpen = true;
      cancelRetries();
      hidePicker();
    },
    true
  );

  document.addEventListener(
    'dialog:open',
    (event) => {
      if (isCartTarget(event.target)) onCartOpened();
    },
    true
  );

  document.addEventListener(
    'dialog:close',
    (event) => {
      if (isCartTarget(event.target)) onCartClosed();
    },
    true
  );

  const onCartChanged = async (event) => {
    /* Capture listener already parked; ensure parked + unlocked. */
    parkPopup();
    attach();
    clearGiftsAvailableCache();
    const items = cartItemsFromEvent(event);
    if (items) applyGiftFromItems(items);
    else await refreshGiftInCart();
    if (!cartIsOpen()) return;
    scheduleEvaluateAfterBogos();
  };

  /* Park before cart morph so the hosted popup is not destroyed mid-update. */
  document.addEventListener('cart:update', parkPopup, true);
  document.addEventListener('cart:updated', parkPopup, true);
  document.addEventListener(
    'quantity-selector:update',
    (event) => {
      if (event.detail?.quantity === 0) parkPopup();
    },
    true
  );
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('.cart-items__remove')) parkPopup();
    },
    true
  );
  document.addEventListener('cart:update', onCartChanged);
  document.addEventListener('cart:updated', onCartChanged);
  document.addEventListener('discount:update', onCartChanged);

  window.addEventListener('pageshow', () => {
    dismissedThisOpen = false;
    parkPopup();
    attach();
    syncCartOpenFromDom();
    if (!cartIsOpen()) hidePicker();
  });

  window.addEventListener('pagehide', () => {
    hidePicker();
  });

  const nodeLooksLikeGiftMount = (node) => {
    if (!(node instanceof Element)) return false;
    if (node.id === POPUP_ID || node.id === PAGE_ID || node.id === FOLD_ID) return true;
    return Boolean(node.querySelector?.(`#${POPUP_ID}, #${PAGE_ID}, #${FOLD_ID}`));
  };

  new MutationObserver((records) => {
    let sawGift = false;
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (nodeLooksLikeGiftMount(node)) {
          sawGift = true;
          break;
        }
      }
      if (sawGift) break;
    }
    if (!sawGift) return;
    attach();
    if (giftQuotaFilled || dismissedThisOpen) {
      hidePicker();
      return;
    }
    if (cartIsOpen() && popupHasGiftCards(popup())) {
      offerAvailable = true;
      scheduleEvaluate();
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      parkPopup();
      attach();
    });
  } else {
    parkPopup();
    attach();
  }
})();
